import { MRT_Cell, MRT_Column, MRT_RowData, MRT_TableInstance } from "material-react-table";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import dayjs, { parseUnlessDate } from "shared/dayjs";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import { t } from "i18next";
import { CreditNoteDTO, PaymentDTO } from "../../state/finance-document-interfaces";
import { Box, TableCellProps } from "@mui/material";
import i18n from "i18n/config";
import { formatDate, getUserTimeZone, getPaymentStatus } from "shared/utils/common";
import SmartTruncatedText from "components/smart-truncated-text";
import { FinanceDocumentStatuses } from "constants/enums";

// Money, quantities, hours and percentages are right-aligned: the digits of a figure then always
// end in the same place, so a column of amounts can be scanned and compared vertically instead of
// read one value at a time. Numbers that are really labels (document, client and article numbers),
// dates and every text column keep the default left alignment, and so do all column headers.
export const numericColumnAlignment: {
  muiTableBodyCellProps: TableCellProps;
  muiTableFooterCellProps: TableCellProps;
} = {
  // For body cells `align` is enough - MRT turns it into the flex justification itself. Deliberately
  // no `sx` here: the position tables set a body-cell `sx` at table level and a column-level one
  // would replace it wholesale.
  muiTableBodyCellProps: { align: 'right' },
  // The total is pushed over with an auto margin rather than with `justify-content`, because
  // `.react-table .MuiPaper-root .MuiBox-root` in _table.scss sets `justify-content: flex-start
  // !important` on every MUI Box inside a list table - which some footers wrap their total in.
  // Auto margins are not affected by that rule, and they collapse to 0 on their own once the
  // content no longer fits, so nothing gets pushed out of view.
  muiTableFooterCellProps: {
    align: 'right',
    sx: { '& > *': { marginLeft: 'auto' } },
  },
};

const getFinanceDocumentStatusName = (statusId: string | number): string => {
  const statusIdString = statusId.toString();
  const statusEntry = Object.entries(FinanceDocumentStatuses).find(([, value]) => value === statusIdString);
  return statusEntry ? statusEntry[0] : statusIdString;
};

export const formatCreditNoteNumber = (creditNote: CreditNoteDTO): string => {
  const approvedStatuses = [
    FinanceDocumentStatuses.Approved,
    FinanceDocumentStatuses.Sent,
    FinanceDocumentStatuses.Done
  ];

  const isApprovedStatus = approvedStatuses.includes(
    creditNote.statusId.toString() as FinanceDocumentStatuses
  );

  if (isApprovedStatus) {
    return creditNote.creditNoteNumber;
  }

  return creditNote.creditNoteNumber
    ? creditNote.creditNoteNumber + " (" + getFinanceDocumentStatusName(creditNote.statusId) + ")"
    : creditNote.statusId.toString();
};

/* export const documentStatusOptions = [
  { value: 'Approved', label: t('Approved') },
  { value: 'Sent', label: t('Sent') },
  { value: 'Received', label: t('Received') },
  { value: 'Cancelled', label: t('Cancelled') },
  { value: 'Rejected', label: t('Rejected') },
];

export const documentDirectionOptions = [
  { value: 'In', label: t('In') },
  { value: 'Out', label: t('Out') },
]; */

export const CellRenderer = <T extends MRT_RowData>({ cell }: { cell: MRT_Cell<T, unknown> }) => {
  const value = cell.getValue<string>();

  return value !== " - " && value ? (
    <Tooltip title={t(value)} placement="right" arrow>
      <span className="truncated-text">{t(value)}</span>
    </Tooltip>
  ) : (
    <span>{value}</span>
  );
};

export const SmartCellRenderer = <T extends MRT_RowData>({ cell }: { cell: MRT_Cell<T, unknown> }) => {
  const value = cell.getValue<string>();

  return value !== " - " && value ? (
    <SmartTruncatedText text={t(value)} />
  ) : (
    <span>{value}</span>
  );
};


export const CellRendererDate = <T extends MRT_RowData>({ cell }: { cell: MRT_Cell<T, unknown> }) => {
  const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
  const value = cell.getValue<Date | string>();
  // The finance and contact columns pass a Date, application-history-tab a string it has already
  // formatted - only the string may be parsed with the user's format.
  const date = parseUnlessDate(value, dateFormat ?? 'DD.MM.YYYY');

  return value ? (
    <Tooltip
      title={date
        .tz(getUserTimeZone())
        .format(dateFormat ?? 'DD.MM.YYYY')}
      placement="right"
      arrow
    >
      <span className="truncated-text break-col-text">
        {date
          .tz(getUserTimeZone())
          .format(dateFormat ?? 'DD.MM.YYYY')}
      </span>
    </Tooltip>
  ) : (
    <span>-</span>
  );
};

// Created/changed timestamps carry a time of day, and the finance document lists are ordered by it
// (newest first), so two documents from the same day are not interchangeable. The column is too
// narrow for a full timestamp, so the cell keeps showing the date and the tooltip reveals the time
// that decides the order.
export const CellRendererDateTime = <T extends MRT_RowData>({ cell }: { cell: MRT_Cell<T, unknown> }) => {
  const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
  const timeFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.timeFormat);
  const value = cell.getValue<Date>();
  const format = dateFormat ?? 'DD.MM.YYYY';

  return value ? (
    <Tooltip
      title={dayjs(value)
        .tz(getUserTimeZone())
        .format(`${format} ${timeFormat ?? 'HH:mm'}`)}
      placement="right"
      arrow
    >
      <span className="truncated-text break-col-text">
        {dayjs(value)
          .tz(getUserTimeZone())
          .format(format)}
      </span>
    </Tooltip>
  ) : (
    <span>-</span>
  );
};

export const CellRendererDateArrow = <T extends MRT_RowData>({ cell }: { cell: MRT_Cell<T, unknown> }) => {
  const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
  const dates = cell.getValue<Date[]>();

  const formattedDates = [...dates].sort().map(date =>
    dayjs(date).tz(getUserTimeZone()).format(dateFormat ?? 'DD.MM.YYYY')
  );

  const tooltipContent = formattedDates.join('\n'); // \n za višelinijski tooltip

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>} placement="right" arrow>
      <div className="truncated-text break-title">
        {formattedDates[formattedDates.length - 1] || " - "}
      </div>
    </Tooltip>
  );
};

export const PaymentsRenderer = <T extends MRT_RowData>({ cell, column, row }: { cell: MRT_Cell<T, unknown>, column: MRT_Column<T, unknown>, row: MRT_RowData }) => {
  const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
  const columnId = column.id;
  const payments = row.original.payments as PaymentDTO[];

  // Make a copy before sorting to avoid mutating Redux state!
  const sortedPayments = [...payments].sort((a, b) => {
    const dateA = a.paidOn instanceof Date ? a.paidOn : new Date(a.paidOn);
    const dateB = b.paidOn instanceof Date ? b.paidOn : new Date(b.paidOn);
    return dateA.getTime() - dateB.getTime();
  });

  const lastPayment = sortedPayments[sortedPayments.length - 1];
  const lastPaymentFormatted = lastPayment && lastPayment.paidOn
    ? dayjs(lastPayment.paidOn).tz(getUserTimeZone()).format(dateFormat ?? 'DD.MM.YYYY')
    : " - ";

  // A payment imported without a payment date (some bank exports carry only the booking date) shows
  // "-" here, not "Invalid date".
  const formattedPaymentDates = sortedPayments.map(payment => formatDate(payment.paidOn, dateFormat)).join('\n');

  const paymentsTooltip = sortedPayments.map(payment =>
    `${(payment.amount + (payment.bankCommission ?? 0))?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}`
  ).join('\n');

  const paymentsSummarized = sortedPayments.reduce((acc, curr) => acc + (curr.amount + (curr.bankCommission ?? 0)), 0);

  return (
    <Tooltip
      disableHoverListener={false}
      title={
        <Box className="paid-paidOn-tooltip">
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Payment date")}</span>
            <span style={{ whiteSpace: 'pre-line' }}>{formattedPaymentDates}</span>
          </Box>
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Amount")}</span>
            <span style={{ whiteSpace: 'pre-line' }}>{paymentsTooltip}</span>
          </Box>
        </Box>
      }
      placement="right"
      arrow
    >
      {columnId === "paidOn" ? (
        <span className="truncated-text break-title">{lastPaymentFormatted}</span>
      ) : (
        <span className="truncated-text break-title">
          {paymentsSummarized === 0
            ? " - "
            : paymentsSummarized?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
        </span>
      )}
    </Tooltip>
  );
};

export const CreditNotesRenderer = <T extends MRT_RowData>({ cell, column, row }: { cell: MRT_Cell<T, unknown>, column: MRT_Column<T, unknown>, row: MRT_RowData }) => {
  const creditNotes = row.original.creditNotes as CreditNoteDTO[];

  if (!creditNotes || creditNotes.length === 0) {
    return <span> - </span>;
  }

  const sortedCreditNotes = [...creditNotes]?.sort((a, b) => a.creditNoteNumber.localeCompare(b.creditNoteNumber));

  const joinedCreditNotes = sortedCreditNotes?.map(creditNote => formatCreditNoteNumber(creditNote)).join('\n');
  const joinedNet = sortedCreditNotes?.map(creditNote => creditNote.net?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })).join('\n');
  const joinedGross = sortedCreditNotes?.map(creditNote => creditNote.gross?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })).join('\n');
  const joinedTax = sortedCreditNotes?.map(creditNote => creditNote.tax?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })).join('\n');

  const creditNotesSummarized = creditNotes.filter(cn => [FinanceDocumentStatuses.Approved, FinanceDocumentStatuses.Sent, FinanceDocumentStatuses.Done].includes(cn.statusId.toString() as FinanceDocumentStatuses)).reduce((acc, curr) => acc + curr.gross, 0);

  return (
    <Tooltip
      disableHoverListener={false}
      placement="right"
      arrow
      slotProps={{
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              maxWidth: 'none',
              width: 'auto'
            }
          }
        }
      }}
      title={
        <Box className="paid-paidOn-tooltip">
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Doc. number")}</span>
            <span style={{ whiteSpace: 'pre' }}>{joinedCreditNotes}</span>
          </Box>
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Net")}</span>
            <span style={{ whiteSpace: 'pre' }}>{joinedNet}</span>
          </Box>
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Gross")}</span>
            <span style={{ whiteSpace: 'pre' }}>{joinedGross}</span>
          </Box>
          <Box display={'flex'} flexDirection="column" gap={1}>
            <span style={{ fontWeight: 'bold' }}>{t("Tax")}</span>
            <span style={{ whiteSpace: 'pre' }}>{joinedTax}</span>
          </Box>
        </Box>
      }>
      <span className="truncated-text break-title">
        {creditNotesSummarized === 0
          ? " - "
          : creditNotesSummarized?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
      </span>
    </Tooltip>
  );
};

export const CellForOpen = <T extends MRT_RowData>({ cell, row }: { cell: MRT_Cell<T, unknown>, row: MRT_RowData }) => {
  const openValue = cell.getValue<number>();
  const original = row.original as T;
  const paymentStatusData = getPaymentStatus(
    original.documentStatus,
    original.totalBrutto,
    original.currency,
    original.payments,
    original.securityDeposit,
    original.depositPercent,
    original.endDate,
    i18n.language,
    original.paidByCash,
    original.creditNotes
  );

  return paymentStatusData.status !== " - " && paymentStatusData.status.length > 0
    ? (
      <Tooltip className="" title={
        <>
          {t("Invoice sum") + ": " + paymentStatusData.invoiceSum?.toLocaleString(i18n.language, { minimumFractionDigits: 2 }) + " " + paymentStatusData.grossCurrency}
          <br />
          {t("Credited sum") + ": " + paymentStatusData.creditedSum?.toLocaleString(i18n.language, { minimumFractionDigits: 2 }) + " " + paymentStatusData.grossCurrency}
          <br />
          {t("Payment sum") + ": " + paymentStatusData.paymentSumInGrossCurrency?.toLocaleString(i18n.language, { minimumFractionDigits: 2 }) + " " + paymentStatusData.grossCurrency}
          <br />
          {t("Not paid") + ": " + openValue?.toLocaleString(i18n.language, { minimumFractionDigits: 2 }) + " " + paymentStatusData.grossCurrency}

          {paymentStatusData.paymentSumInOtherCurrency !== "" && (
            <>
              <br /><br />
              <span className="tootlip-small-text">{t("Payment sums in other currency (not included for status calculation)")}: {paymentStatusData.paymentSumInOtherCurrency}</span>
            </>
          )}
        </>
      } placement="right" arrow>
        <span className="truncated-text break-title">{openValue?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}</span>
      </Tooltip>
    )
    : " - ";
}

export const FooterForOpen = <T extends MRT_RowData>({ table }: { table: MRT_TableInstance<T> }) => {
  const total = table.getFilteredRowModel().rows.reduce((sum, row) => {
    const value = row.getValue<number>('open');
    return sum + value;
  }, 0);

  return (
    <Tooltip className="" title={<span>{`Total across all pages:\n  ${total?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}`}</span>} placement="top" arrow>
      <span className="truncated-text simple-font-color">
        {total?.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
      </span>
    </Tooltip>
  );
}

export const AccessorFnForOpen = <T extends MRT_RowData>(data: T) => {
  var paymentStatusData = getPaymentStatus(data.documentStatus, data.totalBrutto, data.currency, data.payments, data.securityDeposit, data.depositPercent, data.endDate, i18n.language, data.paidByCash, data.creditNotes);
  return paymentStatusData.invoiceSum - paymentStatusData.creditedSum - paymentStatusData.paymentSumInGrossCurrency;
}